import { useMemo } from 'react'
import { useQuery } from 'react-query'
import moment from 'moment'
import APIService from 'api/APIService'
import useLocalStorage from './useLocalStorage'
import { MergeIntegration } from 'types/entity'
import useRolesAndPermissions from './useRolesAndPermissions'

interface StorageValue {
  integrations: MergeIntegration[]
  mappedIntegrations: Record<string, MergeIntegration>
  expiresAt: string
}

const useMergePublic = () => {
  const {
    permissions: { hasFullPower }
  } = useRolesAndPermissions()

  const [localIntegrations, setLocalIntegrations] =
    useLocalStorage<StorageValue>('group_integrations_metadata', {
      integrations: [],
      mappedIntegrations: {},
      expiresAt: ''
    })

  const hasExpired = useMemo(() => {
    if (localIntegrations.expiresAt === '') return true
    const expiresAt = moment(localIntegrations.expiresAt)
    return moment().isAfter(expiresAt)
  }, [localIntegrations])

  const { isLoading } = useQuery({
    queryKey: ['merge', 'public', 'integrations'],
    queryFn: APIService.integrations.getMergeAvailableIntegrations,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    cacheTime: Infinity,
    enabled: hasFullPower && hasExpired,
    onSuccess: (data) => {
      const mappedIntegrations: Record<string, MergeIntegration> = {}
      data.forEach((item: MergeIntegration) => {
        mappedIntegrations[item.slug] = item
      })
      setLocalIntegrations({
        integrations: data,
        mappedIntegrations,
        expiresAt: moment().add(2, 'hours').toISOString()
      })
    }
  })

  return {
    integrations: localIntegrations.integrations,
    mappedIntegrations: localIntegrations.mappedIntegrations,
    isLoading
  }
}

export default useMergePublic
